import styled from '@emotion/styled'
import { App, Modal } from 'antd'
import { CloseOutlined, LeftOutlined } from '@ant-design/icons'
import { Formik, useFormikContext } from 'formik'
import { useHistory } from 'react-router-dom'
import type { AnySchema } from 'yup'
import Text from 'src/components/ts-components/Text'
import SportNinjaLogo from 'src/components/ts-components/SportNinjaLogo'
import CanlanTextLogo from 'src/components/icons/CanlanTextLogo'
import { ROUTES } from '@sportninja/common/constants/app'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { competitionWord } from '@sportninja/common/features/competition/wizard'
import {
  initialWizardValues,
  buildCloneCompetitionPayload,
  buildCreateCompetitionPayload,
  buildClonePayloadFromNewFlow,
  step1Schema,
  step1CloneSchema,
  step2Schema,
  step3Schema,
  step4Schema,
  step4DivisionsSchema,
  step4ConferencesSchema,
  step5Schema,
  wizardSchema,
  isCloneShortCircuit,
  isPlayoffsType,
  toCloneCompetitionType,
  WizardFormValues,
} from '@sportninja/common/features/competition/wizard'
import colors from '@sportninja/common/constants/appColors'
import {
  WizardProvider,
  useWizardNavigation,
  WizardStep,
} from './WizardContext'
import WizardStepsHeader from './WizardStepsHeader'
import WizardFooter from './WizardFooter'
import ConfirmCloseModal from './ConfirmCloseModal'
import Step1Create from './steps/Step1Create'
import Step1CloneForm from './steps/Step1CloneForm'
import Step2Info from './steps/Step2Info'
import Step3Type from './steps/Step3Type'
import Step4Structure from './steps/Step4Structure'
import Step4DivisionsSubStep from './steps/Step4DivisionsSubStep'
import Step4ConferencesSubStep from './steps/Step4ConferencesSubStep'
import Step5Join from './steps/Step5Join'
import Step6Summary from './steps/Step6Summary'
import { useCreateCompetition } from './hooks/useCreateCompetition'
import { useCloneCompetition } from './hooks/useCloneCompetition'

const STEP_SCHEMAS: Record<WizardStep, AnySchema> = {
  create: step1Schema,
  clone: step1CloneSchema,
  info: step2Schema,
  type: step3Schema,
  structure: step4Schema,
  structureDivisions: step4DivisionsSchema,
  structureConferences: step4ConferencesSchema,
  join: step5Schema,
  summary: wizardSchema,
}

/** Subset of the schedule fields the wizard needs to prime the clone branch. */
export type CloneInitialSchedule = {
  id: string
  name_full?: string
  starts_at?: string | null
  ends_at?: string | null
  is_registration_schedule?: boolean
  /** Backend competition type string — pre-fills Competition Type select if it matches a known value. */
  competition_type?: string | null
}

type Props = {
  isOpen: boolean
  orgId: string
  onClose: () => void
  /** Opens the legacy ScheduleModal (RESET_SCHEDULE_FORM). */
  onSkipToLegacy: () => void
  /**
   * When set, the wizard opens directly on the clone step with the source
   * competition pre-selected. The Registration switch reflects the source's
   * is_registration_schedule and is locked.
   */
  cloneInitialSchedule?: CloneInitialSchedule | null
  /** Org's contact email — pre-fills Step 2 Email when the user lands there. */
  orgEmail?: string | null
  /** Org's website — pre-fills Step 2 Website when the user lands there. */
  orgWebsite?: string | null
}

type StepBodyProps = {
  orgId: string
  orgEmail?: string | null
  orgWebsite?: string | null
}

const StepBody = ({ orgId, orgEmail, orgWebsite }: StepBodyProps) => {
  const { values } = useFormikContext<WizardFormValues>()
  const { state } = useWizardNavigation()
  switch (state.currentStep) {
    case 'create':
      return <Step1Create orgId={orgId} />
    case 'clone':
      return <Step1CloneForm orgId={orgId} />
    case 'info':
      return <Step2Info defaultEmail={orgEmail} defaultWebsite={orgWebsite} />
    case 'type':
      return <Step3Type orgId={orgId} />
    case 'structure':
      return <Step4Structure />
    case 'structureDivisions':
      return <Step4DivisionsSubStep competitionName={values.info.name} />
    case 'structureConferences':
      return <Step4ConferencesSubStep competitionName={values.info.name} />
    case 'join':
      return <Step5Join />
    case 'summary':
      return <Step6Summary />
    default:
      return null
  }
}

type WizardBodyProps = Omit<Props, 'isOpen' | 'cloneInitialSchedule'>

const WizardBody = ({
  orgId,
  orgEmail,
  orgWebsite,
  onClose,
  onSkipToLegacy,
}: WizardBodyProps) => {
  const { state, goTo, goBack, requestClose, cancelClose, setSubmitting } =
    useWizardNavigation()
  const formik = useFormikContext<WizardFormValues>()
  const { message } = App.useApp()
  const history = useHistory()
  const createMut = useCreateCompetition()
  const cloneMut = useCloneCompetition()

  const surfaceBackendErrors = (e: unknown) => {
    const fieldErrors = (e as { errors?: Record<string, string[]> }).errors
    if (fieldErrors && typeof fieldErrors === 'object') {
      // Flatten arrays — Formik wants string per field, backend sends string[]
      const flat: Record<string, string> = {}
      for (const [key, value] of Object.entries(fieldErrors)) {
        if (Array.isArray(value) && value[0]) {
          flat[key] = value[0]
        }
      }
      if (Object.keys(flat).length > 0) {
        formik.setErrors(flat as Record<string, string>)
      }
    }
  }

  const slicedValues = (): unknown => {
    switch (state.currentStep) {
      case 'create':
        return { creationMethod: formik.values.creationMethod }
      case 'clone':
        return formik.values.cloneSource
      case 'info':
        return formik.values.info
      case 'type':
        return {
          competitionType: formik.values.competitionType,
          cloneSource: formik.values.cloneSource,
        }
      case 'structure':
        return { structureType: formik.values.structureType }
      case 'structureDivisions':
        return { divisions: formik.values.divisions }
      case 'structureConferences':
        return { conferences: formik.values.conferences }
      case 'join':
        return { joinType: formik.values.joinType }
      default:
        return formik.values
    }
  }

  const validateStep = async (): Promise<boolean> => {
    const schema = STEP_SCHEMAS[state.currentStep]
    try {
      await schema.validate(slicedValues(), { abortEarly: false })
      return true
    } catch (e: unknown) {
      message.error(
        (e as { errors?: string[] }).errors?.[0] ??
          'Please complete this step before continuing'
      )
      return false
    }
  }

  // Simple Playoffs without a clone source skips the Join step — it's a
  // manually-managed bracket by definition, so registration onboarding doesn't
  // apply. Used by the three Structure exit cases below.
  const skipsJoinStep =
    formik.values.competitionType === 'playoffs_simple' &&
    !formik.values.cloneSource.sourceScheduleId
  const afterStructureFlow: WizardStep = skipsJoinStep ? 'summary' : 'join'

  const nextStepAfter = (current: WizardStep): WizardStep => {
    switch (current) {
      case 'create':
        return formik.values.creationMethod === 'clone' ? 'clone' : 'info'
      case 'info':
        return 'type'
      case 'type':
        // Playoffs always short-circuits; Tournament short-circuits when the
        // user picked a clone source. Otherwise we walk through Structure/Join.
        return isCloneShortCircuit(formik.values) ? 'summary' : 'structure'
      case 'structure':
        if (formik.values.structureType === 'multiple') {
          return 'structureDivisions'
        }
        if (formik.values.structureType === 'conferences') {
          return 'structureConferences'
        }
        return afterStructureFlow
      case 'structureDivisions':
      case 'structureConferences':
        return afterStructureFlow
      case 'join':
        return 'summary'
      default:
        return current
    }
  }

  const handlePrimary = async () => {
    if (state.currentStep === 'clone') {
      if (!(await validateStep())) {
        // Reveal inline field errors for required clone fields.
        formik.setTouched(
          {
            cloneSource: {
              sourceScheduleId: true,
              name: true,
              startsAt: true,
              endsAt: true,
              competitionType: true,
            },
          },
          false
        )
        return
      }
      setSubmitting(true)
      try {
        const body = buildCloneCompetitionPayload(formik.values)
        const sourceId = formik.values.cloneSource.sourceScheduleId
        if (!sourceId) {
          return
        } // validateStep() already caught this; invariant
        const cloned = await cloneMut.mutateAsync({
          sourceScheduleId: sourceId,
          body,
          orgId,
        })
        const isRegistration = formik.values.cloneSource.isRegistrationSchedule
        const competitionType = formik.values.cloneSource.competitionType
        formik.resetForm()
        onClose()
        const base = `${ROUTES.SCHEDULE_ROOT}/${cloned.id}`
        if (competitionType === 'playoffs_enhanced') {
          // Enhanced Playoffs needs game slots configured first.
          history.push(`${base}/gameslots`)
        } else {
          history.push(
            isRegistration
              ? `${base}/registration?openRegistrationModal=true`
              : `${base}/setup`
          )
        }
      } catch (e: unknown) {
        surfaceBackendErrors(e)
        message.error((e as Error).message || 'Failed to clone competition')
      } finally {
        setSubmitting(false)
      }
      return
    }

    if (state.currentStep === 'summary') {
      if (!(await validateStep())) {
        return
      }
      setSubmitting(true)
      try {
        // Playoffs route: build a clone payload from the new-flow values and
        // hit the /clone endpoint with the picked source schedule.
        if (isCloneShortCircuit(formik.values)) {
          const sourceId = formik.values.cloneSource.sourceScheduleId
          if (!sourceId) {
            // validateStep() guards this; defensive — release the spinner.
            setSubmitting(false)
            return
          }
          const body = buildClonePayloadFromNewFlow(formik.values)
          const cloned = await cloneMut.mutateAsync({
            sourceScheduleId: sourceId,
            body,
            orgId,
          })
          // Tournament + Registration ON drops the user into the registration
          // onboarding popup, mirroring the dedicated Clone screen behavior.
          const isRegistration = body.is_registration_schedule
          formik.resetForm()
          onClose()
          const base = `${ROUTES.SCHEDULE_ROOT}/${cloned.id}`
          // Enhanced Playoffs needs game slots configured before anything else,
          // so it lands on the gameslots screen instead of setup/registration.
          if (formik.values.competitionType === 'playoffs_enhanced') {
            history.push(`${base}/gameslots`)
            return
          }
          history.push(
            isRegistration
              ? `${base}/registration?openRegistrationModal=true`
              : `${base}/setup`
          )
          return
        }

        const body = buildCreateCompetitionPayload(formik.values)
        const created = await createMut.mutateAsync({ orgId, body })
        const joinType = formik.values.joinType
        formik.resetForm()
        onClose()
        history.push(
          joinType === 'registration'
            ? `${ROUTES.SCHEDULE_ROOT}/${created.id}/registration?openRegistrationModal=true`
            : `${ROUTES.SCHEDULE_ROOT}/${created.id}/setup`
        )
      } catch (e: unknown) {
        surfaceBackendErrors(e)
        message.error((e as Error).message || 'Failed to create competition')
      } finally {
        setSubmitting(false)
      }
      return
    }

    if (!(await validateStep())) {
      return
    }
    goTo(nextStepAfter(state.currentStep))
  }

  const handleSkip = () => handleRequestClose('skip')

  const closeFlow = (intent: 'close' | 'skip') => {
    formik.resetForm()
    cancelClose()
    onClose()
    if (intent === 'skip') {
      onSkipToLegacy()
    }
  }

  const handleRequestClose = (intent: 'close' | 'skip') => {
    const isDirtyPastStep1 = formik.dirty && state.currentStep !== 'create'
    if (isDirtyPastStep1) {
      requestClose(intent)
    } else {
      closeFlow(intent)
    }
  }

  // The Summary step submits either to /wizard/competition (create) or
  // /schedules/:id/clone (Playoffs or Tournament-with-source). The Step 1
  // clone form always submits the dedicated Clone flow.
  const isClonePath =
    state.currentStep === 'clone' ||
    (state.currentStep === 'summary' && isCloneShortCircuit(formik.values))

  const primaryLabel = (() => {
    if (state.currentStep === 'clone') {
      return state.isSubmitting ? 'Cloning...' : 'Clone'
    }
    if (state.currentStep === 'summary') {
      if (state.isSubmitting) {
        return isClonePath ? 'Cloning...' : 'Creating...'
      }
      return isClonePath
        ? `Clone ${competitionWord()}`
        : `Create ${competitionWord()}`
    }
    return 'Next'
  })()

  return (
    <>
      <Layout>
        <TopBar>
          <BackLink
            type='button'
            aria-label='Back'
            onClick={() => {
              if (state.history.length === 0) {
                handleRequestClose('close')
              } else {
                goBack()
              }
            }}
          >
            <LeftOutlined />
            <Text variant='paragraphSmall' color={colors.NEUTRAL_100}>
              Back
            </Text>
          </BackLink>
          <CloseButton
            type='button'
            aria-label='Close wizard'
            onClick={() => handleRequestClose('close')}
          >
            <CloseOutlined />
          </CloseButton>
        </TopBar>
        <LogoBanner>
          {isCanlan ? <CanlanTextLogo height={32} /> : <SportNinjaLogo />}
        </LogoBanner>
        <WizardStepsHeader />
        <StepArea>
          <StepBody orgId={orgId} orgEmail={orgEmail} orgWebsite={orgWebsite} />
        </StepArea>
        <WizardFooter
          primaryLabel={primaryLabel}
          onPrimaryClick={handlePrimary}
          onSkipClick={handleSkip}
          onCancelClick={() => handleRequestClose('close')}
        />
      </Layout>
      <ConfirmCloseModal
        open={state.isConfirmCloseOpen}
        onCancel={cancelClose}
        onConfirm={() => closeFlow(state.closeIntent ?? 'close')}
      />
    </>
  )
}

const CreateCompetitionWizard = ({
  isOpen,
  orgId,
  orgEmail,
  orgWebsite,
  onClose,
  onSkipToLegacy,
  cloneInitialSchedule,
}: Props) => {
  const resolvedType = cloneInitialSchedule
    ? toCloneCompetitionType(cloneInitialSchedule.competition_type)
    : null
  // Playoffs (Simple or Enhanced) can never be a registration competition —
  // ignore the source's registration flag so the locked toggle in Step 1
  // CloneForm matches the data we'd send on submit.
  const initialValues: WizardFormValues = cloneInitialSchedule
    ? {
        ...initialWizardValues,
        creationMethod: 'clone',
        cloneSource: {
          ...initialWizardValues.cloneSource,
          sourceScheduleId: cloneInitialSchedule.id,
          sourceName: cloneInitialSchedule.name_full ?? null,
          name: cloneInitialSchedule.name_full
            ? `Copy of ${cloneInitialSchedule.name_full}`
            : '',
          startsAt: cloneInitialSchedule.starts_at ?? null,
          endsAt: cloneInitialSchedule.ends_at ?? null,
          competitionType: resolvedType,
          isTournament: resolvedType === 'playoffs_enhanced',
          isRegistrationSchedule: isPlayoffsType(resolvedType)
            ? false
            : Boolean(cloneInitialSchedule.is_registration_schedule),
        },
      }
    : initialWizardValues

  return (
    <Modal
      open={isOpen}
      closable={false}
      destroyOnClose
      footer={null}
      maskClosable={false}
      width='100vw'
      zIndex={10000}
      style={{ top: 0, padding: 0, maxWidth: '100vw' }}
      styles={{
        body: {
          padding: 0,
          background: colors.SECONDARY_500,
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        },
        content: {
          padding: 0,
          background: colors.SECONDARY_500,
          height: '100vh',
          borderRadius: 0,
        },
        mask: { backgroundColor: colors.SECONDARY_500 },
      }}
    >
      <Formik<WizardFormValues>
        initialValues={initialValues}
        validationSchema={wizardSchema}
        validateOnMount={false}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={() => {
          /* submit handled imperatively in WizardBody */
        }}
      >
        <WizardProvider initialStep={cloneInitialSchedule ? 'clone' : 'create'}>
          <WizardBody
            orgId={orgId}
            orgEmail={orgEmail}
            orgWebsite={orgWebsite}
            onClose={onClose}
            onSkipToLegacy={onSkipToLegacy}
          />
        </WizardProvider>
      </Formik>
    </Modal>
  )
}

export default CreateCompetitionWizard

// ---- styled ----
const Layout = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 1200px;
  height: 90%;
  margin: auto;
`

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
`

const BackLink = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: transparent;
  border: 0;
  color: ${colors.NEUTRAL_100};
  cursor: pointer;
  padding: 0;
`

const LogoBanner = styled.div`
  display: flex;
  justify-content: flex-start;
  padding: 8px 16px 16px;
  svg {
    width: 190px;
    height: 43px;
  }
`

const CloseButton = styled.button`
  background: transparent;
  border: 0;
  color: ${colors.NEUTRAL_50};
  cursor: pointer;
  font-size: 18px;
`

const StepArea = styled.div`
  overflow-y: auto;
  padding: 48px 24px 0;
`
